import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { API_BASE } from '../../../config/api';
import {
  buildDisplayMapPoints,
  formatTimeLeft,
  formatUnixTime,
  getSessionDurationLabel,
  normalizeTimestampSeconds,
  prettyDataForDisplay,
} from '../analyticsUtils';
import {
  Card,
  CardHead,
  DangerBtn,
  Empty,
  GhostBtn,
  IconBtn,
  Mono,
  Notice,
  Segment,
  Segmented,
  Select,
  Skeleton,
} from '../AnalyticsAdmin.styles';

/* ---------- helpers ---------- */

function timeAgo(value) {
  const seconds = normalizeTimestampSeconds(value);
  if (!seconds) return '';
  const diff = Math.max(0, Math.floor(Date.now() / 1000) - seconds);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
  return `${Math.floor(diff / 86400)} d ago`;
}

const POINT_COLORS = {
  focused: '#F5C542',
  first: '#3DDC84',
  last: '#FFFFFF',
  action: '#FF7A2E',
  position: '#4F8CFF',
};

/* ---------- styles ---------- */

const Layout = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem;
  align-items: flex-start;
`;

const ListPane = styled(Card)`
  flex: 1 1 22rem;
  min-width: 0;
`;

const DetailPane = styled.section`
  flex: 999 1 32rem;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SessionList = styled.ul`
  list-style: none;
`;

const SessionButton = styled.button`
  display: block;
  width: 100%;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--border-subtle);
  text-align: left;
  font-size: 0.875rem;
  color: var(--text-primary);
  background: ${(p) => (p.$active ? '#1a1d22' : 'transparent')};
  box-shadow: ${(p) => (p.$active ? 'inset 3px 0 0 var(--brand)' : 'none')};

  &:hover {
    background: #15181d;
  }

  > span {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 0.25rem 0.875rem;
  }

  > span + span {
    justify-content: flex-start;
    margin-top: 0.25rem;
    font-size: 0.75rem;
    color: var(--text-secondary);
  }
`;

const Pager = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 1rem;
  font-size: 0.8125rem;
  color: var(--text-tertiary);
`;

const Facts = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(7.5rem, 1fr));
  gap: 0.75rem;
  margin-top: 1rem;

  span {
    display: block;
    font-size: 0.75rem;
    color: var(--text-tertiary);
  }

  b {
    font-weight: 600;
  }
`;

const MapFrame = styled.div`
  position: relative;
  background: #0e1a14;
  overflow: hidden;
  min-height: 12rem;
  cursor: grab;

  &[data-dragging='1'] {
    cursor: grabbing;
  }
`;

const MapOverlay = styled.div`
  position: absolute;
  ${(p) => (p.$right ? 'right: 10px;' : 'left: 10px;')}
  top: 10px;
  display: flex;
  flex-direction: ${(p) => (p.$right ? 'column' : 'row')};
  gap: 4px;
  z-index: 2;
`;

const PlayerBar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem 1rem;
  border-top: 1px solid var(--border);
`;

const PlayButton = styled.button`
  width: 2.75rem;
  height: 2.25rem;
  border-radius: 0.375rem;
  background: var(--brand);
  color: var(--brand-ink);
  font-weight: 700;
`;

const Progress = styled.div`
  flex-grow: 1;
  height: 4px;
  border-radius: 2px;
  background: var(--border);
  overflow: hidden;

  span {
    display: block;
    height: 100%;
    background: var(--brand);
  }
`;

const TimelineItemButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.5625rem 1rem;
  font-size: 0.875rem;
  text-align: left;
  color: var(--text-primary);
  background: ${(p) => (p.$focused ? '#1a1d22' : 'transparent')};

  &:hover {
    background: #15181d;
  }
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${(p) => p.$color};
`;

const JsonBlock = styled.pre`
  margin: 0 1rem 0.625rem 6.125rem;
  padding: 0.75rem 0.875rem;
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: 0.375rem;
  font-family: var(--font-mono);
  font-size: 0.75rem;
  color: var(--text-code);
  max-height: 14rem;
  overflow: auto;
  white-space: pre-wrap;

  @media (max-width: 600px) {
    margin-left: 1rem;
  }
`;

/* ---------- timeline row ---------- */

const TimelineActionRow = memo(function TimelineActionRow({ item, isOpen, isFocused, onToggle, onFocus }) {
  const hasData = item.data && Object.keys(item.data).length > 0;
  const prettyJson = useMemo(
    () => (hasData && isOpen ? JSON.stringify(prettyDataForDisplay(item.data), null, 2) : null),
    [hasData, isOpen, item.data]
  );

  return (
    <li>
      <TimelineItemButton
        type="button"
        $focused={isFocused}
        aria-expanded={hasData ? isOpen : undefined}
        onClick={() => {
          if (hasData) onToggle(item.key);
          onFocus(item.key);
        }}
      >
        <Mono style={{ width: '4.5rem', flexShrink: 0 }}>{formatUnixTime(item.t)}</Mono>
        <Dot $color={isFocused ? POINT_COLORS.focused : POINT_COLORS.action} />
        <span style={{ flexGrow: 1, minWidth: 0, fontWeight: 500 }}>{item.actionType}</span>
        <Mono style={{ color: '#4a4f58' }}>flush {item.flushId}</Mono>
      </TimelineItemButton>
      {prettyJson && <JsonBlock>{prettyJson}</JsonBlock>}
    </li>
  );
});

/* ---------- map with replay ---------- */

export const SessionMap = memo(function SessionMap({
  projectMap,
  mapLoading,
  mapVersion,
  userToken,
  setMapImageSize,
  mapImageSize,
  projectedMapPoints,
  selectedActionMapKey,
  debugMapPoint,
  viewportResetKey,
  showPlayer = true,
  onOpenMapSettings,
}) {
  const [overlay, setOverlay] = useState({ zoom: 1, pan: { x: 0, y: 0 } });
  const [pointSourceMode, setPointSourceMode] = useState('all');
  const [replayStep, setReplayStep] = useState(null); // null = whole path
  const [playing, setPlaying] = useState(false);
  const [fast, setFast] = useState(false);
  const viewportRef = useRef(null);
  const transformRef = useRef(null);
  const panRef = useRef({ x: 0, y: 0 });
  const zoomRef = useRef(1);
  const dragRef = useRef(null);
  const frameRef = useRef(null);
  const timerRef = useRef(null);

  const apply = useCallback((pan, zoom) => {
    panRef.current = pan;
    zoomRef.current = zoom;
    if (transformRef.current) transformRef.current.style.transform = `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`;
    setOverlay({ zoom, pan });
  }, []);

  const resetView = useCallback(() => apply({ x: 0, y: 0 }, 1), [apply]);

  const zoomBy = useCallback((delta, cx, cy) => {
    const zoom = zoomRef.current;
    const next = Math.min(20, Math.max(0.5, Number((zoom + delta).toFixed(2))));
    if (next === zoom) return;
    const rect = viewportRef.current?.getBoundingClientRect();
    const mx = cx ?? (rect ? rect.width / 2 : 0);
    const my = cy ?? (rect ? rect.height / 2 : 0);
    const pan = panRef.current;
    apply({ x: mx - ((mx - pan.x) / zoom) * next, y: my - ((my - pan.y) / zoom) * next }, next);
  }, [apply]);

  // Native non-passive listener so wheel-zoom does not also scroll the page.
  const hasImage = Boolean(projectMap?.hasImage);
  useEffect(() => {
    const node = viewportRef.current;
    if (!node) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const rect = node.getBoundingClientRect();
      zoomBy(e.deltaY < 0 ? 0.2 : -0.2, e.clientX - rect.left, e.clientY - rect.top);
    };
    node.addEventListener('wheel', onWheel, { passive: false });
    return () => node.removeEventListener('wheel', onWheel);
  }, [zoomBy, hasImage, mapLoading]);

  const onMouseMove = useCallback((e) => {
    const drag = dragRef.current;
    if (!drag) return;
    const pan = { x: drag.originX + e.clientX - drag.startX, y: drag.originY + e.clientY - drag.startY };
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    frameRef.current = requestAnimationFrame(() => apply(pan, zoomRef.current));
  }, [apply]);

  const onMouseUp = useCallback(() => {
    dragRef.current = null;
    viewportRef.current?.removeAttribute('data-dragging');
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
  }, [onMouseMove]);

  const onMouseDown = (e) => {
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, originX: panRef.current.x, originY: panRef.current.y };
    viewportRef.current?.setAttribute('data-dragging', '1');
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const stopTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    setPlaying(false);
  };

  useEffect(() => {
    resetView();
    setPointSourceMode('all');
    setReplayStep(null);
    stopTimer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetView, viewportResetKey]);

  // Focusing a timeline action shows the path up to it, so drop any partial replay.
  useEffect(() => {
    setReplayStep(null);
    stopTimer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedActionMapKey]);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (frameRef.current) cancelAnimationFrame(frameRef.current);
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
  }, [onMouseMove, onMouseUp]);

  const sourcePoints = useMemo(() => {
    if (!Array.isArray(projectedMapPoints)) return [];
    return pointSourceMode === 'positions' ? projectedMapPoints.filter((p) => p.source === 'position') : projectedMapPoints;
  }, [projectedMapPoints, pointSourceMode]);

  const displayed = useMemo(() => buildDisplayMapPoints(sourcePoints, selectedActionMapKey), [sourcePoints, selectedActionMapKey]);
  const total = displayed.length;
  const shown = replayStep == null ? displayed : displayed.slice(0, Math.min(replayStep, total));

  const screen = shown.map((p) => ({
    ...p,
    sx: p.map.x * overlay.zoom + overlay.pan.x,
    sy: p.map.y * overlay.zoom + overlay.pan.y,
  }));
  const polyline = screen.map((p) => `${p.sx},${p.sy}`).join(' ');

  const startTimer = (speedFast) => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setReplayStep((step) => {
        const next = (step ?? 0) + 1;
        if (next >= total) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          setPlaying(false);
          return total;
        }
        return next;
      });
    }, speedFast ? 40 : 160);
    setPlaying(true);
  };

  const togglePlay = () => {
    if (playing) {
      stopTimer();
      return;
    }
    if (replayStep == null || replayStep >= total) setReplayStep(0);
    startTimer(fast);
  };

  const toggleFast = () => {
    setFast((f) => {
      if (playing) startTimer(!f);
      return !f;
    });
  };

  const step = (delta) => {
    stopTimer();
    setReplayStep((s) => Math.max(0, Math.min(total, (s ?? total) + delta)));
  };

  if (mapLoading) {
    return (
      <Card $flush>
        <Skeleton $h="18rem" $r="0" />
      </Card>
    );
  }

  if (!projectMap?.hasImage) {
    return (
      <Empty $pad="2.5rem 1.5rem">
        <strong>No map yet</strong>
        <p style={{ marginBottom: onOpenMapSettings ? '1rem' : 0 }}>
          Upload a top-down image of your place to see where players go.
        </p>
        {onOpenMapSettings && (
          <GhostBtn type="button" onClick={onOpenMapSettings}>
            Configure the map
          </GhostBtn>
        )}
      </Empty>
    );
  }

  const pct = replayStep == null ? 100 : total ? Math.round((replayStep / total) * 100) : 0;
  const currentPoint = replayStep ? shown[shown.length - 1] : null;

  return (
    <Card $flush>
      <MapFrame ref={viewportRef} onMouseDown={onMouseDown}>
        <div style={{ position: 'relative', width: '100%', maxWidth: mapImageSize.width || '100%' }}>
          <div
            ref={transformRef}
            style={{ transformOrigin: 'top left', willChange: 'transform', transform: `translate(${overlay.pan.x}px, ${overlay.pan.y}px) scale(${overlay.zoom})` }}
          >
            <img
              src={`${API_BASE}${projectMap.imageUrl}?v=${mapVersion}&token=${encodeURIComponent(userToken || '')}`}
              alt="Project map"
              draggable={false}
              style={{ display: 'block', width: '100%', height: 'auto' }}
              onLoad={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setMapImageSize({ width: rect.width / zoomRef.current, height: rect.height / zoomRef.current });
              }}
            />
          </div>
          <svg
            width={mapImageSize.width}
            height={mapImageSize.height}
            style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none', overflow: 'visible' }}
            aria-hidden="true"
          >
            {screen.length > 1 && (
              <>
                <polyline points={polyline} fill="none" stroke="rgba(10,11,13,0.85)" strokeWidth={6} strokeLinecap="round" strokeLinejoin="round" />
                <polyline points={polyline} fill="none" stroke="#FF7A2E" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
              </>
            )}
            {screen.map((p, i) => {
              const isFirst = i === 0;
              const isLast = i === screen.length - 1;
              const isFocused = selectedActionMapKey && p.actionKey === selectedActionMapKey;
              const fill = isFocused
                ? POINT_COLORS.focused
                : isFirst
                  ? POINT_COLORS.first
                  : isLast
                    ? POINT_COLORS.last
                    : p.source === 'action'
                      ? POINT_COLORS.action
                      : POINT_COLORS.position;
              const big = isFirst || isLast || isFocused;
              return (
                <g key={p.key}>
                  <circle cx={p.sx} cy={p.sy} r={big ? 7 : 4} fill={fill} stroke="#0A0B0D" strokeWidth={2} />
                  {big && (
                    <text
                      x={p.sx}
                      y={p.sy - 13}
                      textAnchor="middle"
                      fontSize={10}
                      fontWeight="700"
                      fill="#fff"
                      stroke="rgba(10,11,13,0.9)"
                      strokeWidth={3}
                      paintOrder="stroke"
                    >
                      {isFocused ? 'FOCUS' : isFirst ? 'START' : 'END'}
                    </text>
                  )}
                </g>
              );
            })}
            {debugMapPoint && (
              <g>
                <circle
                  cx={debugMapPoint.x * overlay.zoom + overlay.pan.x}
                  cy={debugMapPoint.y * overlay.zoom + overlay.pan.y}
                  r={14}
                  fill="rgba(240,82,82,0.18)"
                  stroke="#F05252"
                  strokeWidth="2"
                  strokeDasharray="4 4"
                />
                <circle
                  cx={debugMapPoint.x * overlay.zoom + overlay.pan.x}
                  cy={debugMapPoint.y * overlay.zoom + overlay.pan.y}
                  r={6}
                  fill="#F05252"
                  stroke="#fff"
                  strokeWidth="2"
                />
              </g>
            )}
          </svg>
        </div>

        {showPlayer && (
          <MapOverlay onMouseDown={(e) => e.stopPropagation()}>
            <Segmented role="group" aria-label="Points shown" style={{ background: 'rgba(10,11,13,0.85)' }}>
              <Segment type="button" $active={pointSourceMode === 'all'} aria-pressed={pointSourceMode === 'all'} onClick={() => setPointSourceMode('all')}>
                All points
              </Segment>
              <Segment
                type="button"
                $active={pointSourceMode === 'positions'}
                aria-pressed={pointSourceMode === 'positions'}
                onClick={() => setPointSourceMode('positions')}
              >
                Positions only
              </Segment>
            </Segmented>
          </MapOverlay>
        )}
        <MapOverlay $right onMouseDown={(e) => e.stopPropagation()}>
          <IconBtn type="button" aria-label="Zoom in" onClick={() => zoomBy(0.5)}>+</IconBtn>
          <IconBtn type="button" aria-label="Zoom out" onClick={() => zoomBy(-0.5)}>−</IconBtn>
          <IconBtn type="button" aria-label="Reset view" onClick={resetView}>⟲</IconBtn>
        </MapOverlay>
      </MapFrame>

      {showPlayer && (
        <PlayerBar>
          <IconBtn type="button" aria-label="Previous point" onClick={() => step(-1)} disabled={total === 0 || replayStep === 0}>⏮</IconBtn>
          <PlayButton type="button" aria-label={playing ? 'Pause replay' : 'Play replay'} onClick={togglePlay} disabled={total === 0}>
            {playing ? '❚❚' : '▶'}
          </PlayButton>
          <IconBtn type="button" aria-label="Next point" onClick={() => step(1)} disabled={total === 0 || replayStep == null || replayStep >= total}>⏭</IconBtn>
          <IconBtn type="button" aria-pressed={fast} aria-label="Fast replay" onClick={toggleFast} style={{ width: 'auto', padding: '0 0.5rem', fontSize: '0.75rem', color: fast ? 'var(--brand)' : undefined }}>
            {fast ? '4×' : '1×'}
          </IconBtn>
          <Progress aria-hidden="true">
            <span style={{ width: `${pct}%` }} />
          </Progress>
          <Mono $bright style={{ whiteSpace: 'nowrap' }}>
            {total === 0
              ? 'No positions in this session'
              : replayStep == null
                ? `${total} points`
                : `Step ${replayStep} / ${total}${currentPoint?.t ? ` · ${new Date(currentPoint.t * 1000).toLocaleTimeString()}` : ''}`}
          </Mono>
        </PlayerBar>
      )}
    </Card>
  );
});

/* ---------- view ---------- */

const SORT_OPTIONS = [
  { value: 'updated_at', label: 'Most recent' },
  { value: 'duration', label: 'Longest' },
  { value: 'actions', label: 'Most actions' },
  { value: 'game', label: 'Place' },
];

const SessionsView = ({
  sessions,
  sessionsLoading,
  sessionsTotalCount,
  sessionsPage,
  sessionsPerPage,
  sessionsTotalPages,
  sessionsSortBy,
  sessionsSortDir,
  onChangeList,
  expandedSession,
  selectedSession,
  flushesLoading,
  flushCount,
  timeline,
  sessionExpiry,
  onOpenSession,
  onDeleteSession,
  actionTimelineItems,
  pagedTimelineItems,
  timelinePage,
  timelineTotalPages,
  setTimelinePage,
  expandedTimelineItems,
  selectedActionMapKey,
  onToggleTimelineItem,
  onFocusTimelineItem,
  mapProps,
  onOpenMapSettings,
}) => {
  const firstIndex = (sessionsPage - 1) * sessionsPerPage;

  return (
    <Layout>
      <ListPane $flush aria-label="Sessions">
        <CardHead>
          <Mono>{sessionsTotalCount.toLocaleString()} sessions</Mono>
          <div style={{ display: 'flex', gap: '0.375rem' }}>
            <Select
              aria-label="Sort sessions"
              value={sessionsSortBy}
              onChange={(e) => onChangeList({ page: 1, sortBy: e.target.value })}
              style={{ minHeight: '2.125rem', fontSize: '0.8125rem' }}
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Select>
            <GhostBtn
              type="button"
              $small
              aria-label={sessionsSortDir === 'desc' ? 'Sorted descending, switch to ascending' : 'Sorted ascending, switch to descending'}
              onClick={() => onChangeList({ page: 1, sortDir: sessionsSortDir === 'desc' ? 'asc' : 'desc' })}
            >
              {sessionsSortDir === 'desc' ? '↓' : '↑'}
            </GhostBtn>
          </div>
        </CardHead>

        {sessionsLoading ? (
          <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1.125rem' }} aria-busy="true">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Skeleton $w="45%" />
                <Skeleton $w="75%" $h="0.625rem" />
              </div>
            ))}
          </div>
        ) : sessions.length === 0 ? (
          <div style={{ padding: '2.5rem 1.25rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
            No sessions yet. A session appears here when your game sends its first flush.
          </div>
        ) : (
          <SessionList>
            {sessions.map((s, index) => {
              const active = expandedSession === s.session_id;
              return (
                <li key={s.session_id}>
                  <SessionButton type="button" $active={active} aria-pressed={active} onClick={() => onOpenSession(s.session_id)}>
                    <span>
                      <b>Session #{firstIndex + index + 1}</b>
                      <Mono>{timeAgo(s.updated_at ?? s.quit_time ?? s.join_time)}</Mono>
                    </span>
                    <span>
                      {s.user_id && <Mono $bright>user {s.user_id}</Mono>}
                      {s.game_name && <span>{s.game_name}</span>}
                      <span>{getSessionDurationLabel(s, active ? timeline : [])}</span>
                      <span>{Number(s.action_events_count || 0).toLocaleString()} actions</span>
                    </span>
                  </SessionButton>
                </li>
              );
            })}
          </SessionList>
        )}

        <Pager>
          <span>
            {sessionsTotalCount === 0
              ? '0 sessions'
              : `${firstIndex + 1}–${Math.min(firstIndex + sessionsPerPage, sessionsTotalCount)} of ${sessionsTotalCount.toLocaleString()}`}
          </span>
          <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <Select
              aria-label="Sessions per page"
              value={String(sessionsPerPage)}
              onChange={(e) => onChangeList({ page: 1, perPage: Number(e.target.value) || 25 })}
              style={{ minHeight: '2.125rem', fontSize: '0.8125rem' }}
            >
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>{n} / page</option>
              ))}
            </Select>
            <IconBtn type="button" aria-label="Previous page" disabled={sessionsPage <= 1} onClick={() => onChangeList({ page: sessionsPage - 1 })}>‹</IconBtn>
            <IconBtn type="button" aria-label="Next page" disabled={sessionsPage >= sessionsTotalPages} onClick={() => onChangeList({ page: sessionsPage + 1 })}>›</IconBtn>
          </span>
        </Pager>
      </ListPane>

      <DetailPane aria-label="Session detail">
        {!expandedSession ? (
          <Empty>
            <strong>Pick a session to replay it</strong>
            The map shows the path of the player, and the timeline lists each action in order.
          </Empty>
        ) : (
          <>
            <Card>
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem' }}>
                <div style={{ minWidth: 0 }}>
                  <h2 style={{ fontSize: '1.375rem', marginBottom: 2 }}>
                    Session #{firstIndex + Math.max(0, sessions.findIndex((s) => s.session_id === expandedSession)) + 1}
                  </h2>
                  <Mono style={{ wordBreak: 'break-all' }}>
                    {expandedSession}
                    {selectedSession?.user_id ? ` · user ${selectedSession.user_id}` : ''}
                  </Mono>
                </div>
                <DangerBtn type="button" $small onClick={() => onDeleteSession(expandedSession)}>
                  Delete
                </DangerBtn>
              </div>
              <Facts>
                <div><span>Duration</span><b>{selectedSession ? getSessionDurationLabel(selectedSession, timeline) : '-'}</b></div>
                <div><span>Actions</span><b>{Number(selectedSession?.action_events_count || actionTimelineItems.length).toLocaleString()}</b></div>
                <div><span>Place</span><b>{selectedSession?.game_name || '-'}</b></div>
                <div>
                  <span>Raw data expires</span>
                  <b title={sessionExpiry ? sessionExpiry.toLocaleString() : undefined}>{sessionExpiry ? formatTimeLeft(sessionExpiry) : '-'}</b>
                </div>
              </Facts>
            </Card>

            <SessionMap {...mapProps} onOpenMapSettings={onOpenMapSettings} />

            <Card $flush>
              <CardHead>
                <h2>Timeline</h2>
                <Mono>Click an action to focus the map there</Mono>
              </CardHead>
              {flushesLoading ? (
                <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }} aria-busy="true">
                  {[0, 1, 2, 3].map((i) => <Skeleton key={i} $w={`${60 - i * 8}%`} />)}
                </div>
              ) : flushCount === 0 ? (
                <div style={{ padding: '1.5rem 1rem' }}>
                  <Notice>No raw data for this session. It may have expired.</Notice>
                </div>
              ) : actionTimelineItems.length === 0 ? (
                <div style={{ padding: '1.5rem 1rem', color: 'var(--text-secondary)' }}>No actions recorded in this session.</div>
              ) : (
                <>
                  <ol style={{ listStyle: 'none', padding: '0.375rem 0' }}>
                    {pagedTimelineItems.map((item) => (
                      <TimelineActionRow
                        key={item.key}
                        item={item}
                        isOpen={Boolean(expandedTimelineItems[item.key])}
                        isFocused={selectedActionMapKey === item.key}
                        onToggle={onToggleTimelineItem}
                        onFocus={onFocusTimelineItem}
                      />
                    ))}
                  </ol>
                  {timelineTotalPages > 1 && (
                    <Pager style={{ borderTop: '1px solid var(--border)' }}>
                      <span>
                        Page {timelinePage} of {timelineTotalPages} · {actionTimelineItems.length.toLocaleString()} actions
                      </span>
                      <span style={{ display: 'flex', gap: 6 }}>
                        <IconBtn type="button" aria-label="Previous timeline page" disabled={timelinePage <= 1} onClick={() => setTimelinePage((p) => Math.max(1, p - 1))}>‹</IconBtn>
                        <IconBtn type="button" aria-label="Next timeline page" disabled={timelinePage >= timelineTotalPages} onClick={() => setTimelinePage((p) => Math.min(timelineTotalPages, p + 1))}>›</IconBtn>
                      </span>
                    </Pager>
                  )}
                </>
              )}
            </Card>
          </>
        )}
      </DetailPane>
    </Layout>
  );
};

export default SessionsView;
